import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Check, Flame } from 'lucide-react';

export const Portal = ({ color, open, size = 120, label }) => (
  <div className="relative flex flex-col items-center" data-testid={`portal-${open ? 'open' : 'closed'}`}>
    <div className="relative" style={{ width: size, height: size }}>
      {[0, 1, 2].map(i => (
        <motion.span key={i} className="absolute inset-0 rounded-full border-2"
          style={{ borderColor: color, opacity: open ? 0.9 - i * 0.25 : 0.25 - i * 0.06 }}
          animate={open ? { rotate: i % 2 ? -360 : 360, scale: [1 - i * 0.12, 1.05 - i * 0.12, 1 - i * 0.12] } : { rotate: i % 2 ? -360 : 360 }}
          transition={{ duration: 14 - i * 3, repeat: Infinity, ease: 'linear' }} />
      ))}
      <motion.div className="absolute inset-[18%] rounded-full"
        style={{ background: open ? `radial-gradient(circle, #fff 0%, ${color} 35%, transparent 72%)` : `radial-gradient(circle, ${color}33 0%, transparent 70%)`, filter: open ? 'blur(2px)' : 'blur(6px)' }}
        animate={open ? { scale: [0.9, 1.1, 0.9], opacity: [0.8, 1, 0.8] } : { scale: [0.8, 0.95, 0.8], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 3, repeat: Infinity }} />
      {!open && <Lock className="absolute inset-0 m-auto w-6 h-6 text-zinc-500" />}
    </div>
    {label && <p className="mt-2 text-[10px] uppercase tracking-[0.3em]" style={{ color: open ? color : '#71717a' }}>{label}</p>}
  </div>
);

export const WeekNode = ({ w, side, onClick, index }) => {
  const color = w.phase.color;
  const locked = w.status === 'locked';
  const active = w.status === 'active';
  return (
    <motion.button
      data-testid={`journey-week-${w.week}`}
      initial={{ opacity: 0, x: side === 'left' ? -40 : 40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ type: 'spring', stiffness: 160, damping: 20, delay: 0.05 * (index % 3) }}
      whileHover={!locked ? { scale: 1.04 } : {}}
      onClick={() => onClick(w)}
      className={`relative flex items-center gap-4 w-[min(92vw,420px)] text-left rounded-2xl p-4 border backdrop-blur-md transition-colors
        ${side === 'left' ? 'self-start ml-2 sm:ml-[8%]' : 'self-end mr-2 sm:mr-[8%]'}
        ${locked ? 'border-zinc-800 bg-zinc-950/60' : 'bg-black/50'}`}
      style={!locked ? { borderColor: `${color}55`, boxShadow: active ? `0 0 50px ${color}55` : `0 0 20px ${color}1a` } : {}}
    >
      <div className="relative shrink-0">
        <div className={`w-14 h-14 rounded-full flex items-center justify-center text-xl font-black ${locked ? 'bg-zinc-900 text-zinc-600 border border-zinc-800' : 'text-black'}`}
          style={!locked ? { background: w.status === 'closed' ? color : `linear-gradient(135deg, ${color}, #fff8)` } : {}}>
          {w.status === 'closed' ? <Check className="w-6 h-6" /> : locked ? <Lock className="w-5 h-5" /> : w.week}
        </div>
        {active && (
          <motion.span data-testid="journey-player" className="absolute -inset-2 rounded-full border-2" style={{ borderColor: color }}
            animate={{ scale: [1, 1.35], opacity: [0.8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-[10px] uppercase tracking-[0.25em] ${locked ? 'text-zinc-600' : 'text-zinc-500'}`}>Semana {w.week}{w.is_gate && ' · Portal'}</p>
        <p className={`font-bold truncate ${locked ? 'text-zinc-500 blur-[1.5px]' : 'text-white'}`}>{w.emoji} {w.title_pt}</p>
        <p className={`text-xs mt-0.5 line-clamp-2 ${locked ? 'text-zinc-700 blur-[2px] select-none' : 'text-zinc-400'}`}>{w.focus_pt}</p>
      </div>
      {active && <Flame className="w-5 h-5 shrink-0" style={{ color }} />}
    </motion.button>
  );
};

export const TerritoryHeader = ({ phase, index }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative my-10 text-center" data-testid={`territory-${phase.id}`}>
    <p className="text-[10px] uppercase tracking-[0.5em]" style={{ color: phase.color }}>Território {index + 1}</p>
    <h2 className="text-2xl sm:text-3xl font-black text-white mt-1" style={{ textShadow: `0 0 30px ${phase.color}66` }}>{phase.name_pt}</h2>
    {phase.passed && <p className="text-[11px] text-zinc-400 mt-1">✓ Portal atravessado</p>}
  </motion.div>
);
